import { useCallback, useEffect, useRef, useState } from "react";

import { post } from "lib/api";
import { countryForCompany } from "lib/companyProfile";
import { readStorefrontTenantKey } from "lib/storefront";

export const MIN_ADDRESS_LOOKUP_CHARS = 5;

const ADDRESS_LOOKUP_DEBOUNCE_MS = 350;

function trimmed(value) {
  return String(value || "").trim();
}

function trimmedLower(value) {
  return trimmed(value).toLowerCase();
}

function createLocalId() {
  if (typeof globalThis !== "undefined" && globalThis.crypto?.randomUUID) {
    return `local-${globalThis.crypto.randomUUID()}`;
  }
  return `local-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function defaultAddressCountry(companyKey) {
  const resolvedKey =
    trimmed(companyKey).toLowerCase() ||
    readStorefrontTenantKey() ||
    (typeof window !== "undefined"
      ? trimmed(window.localStorage.getItem("activeCompany")).toLowerCase()
      : "");
  return countryForCompany(resolvedKey);
}

export function createAddressRow(companyKey, overrides = {}) {
  return {
    clientId: createLocalId(),
    id: null,
    addressId: null,
    label: "",
    name: "",
    formattedAddress: "",
    placeId: "",
    businessName: "",
    latitude: null,
    longitude: null,
    googleMapsUri: "",
    street: "",
    district: "",
    city: "",
    state: "",
    postalCode: "",
    country: defaultAddressCountry(companyKey),
    email: "",
    phone: "",
    isPrimary: false,
    ...overrides,
  };
}

export function normalizeAddressRow(address, companyKey) {
  const base = createAddressRow(companyKey);
  return {
    ...base,
    ...address,
    clientId: trimmed(address?.clientId) || base.clientId,
    id: address?.id ?? null,
    addressId: trimmed(address?.addressId) || null,
    label: trimmed(address?.label),
    name: trimmed(address?.name),
    formattedAddress: trimmed(address?.formattedAddress),
    placeId: trimmed(address?.placeId),
    businessName: trimmed(address?.businessName),
    latitude:
      address?.latitude != null && Number.isFinite(Number(address.latitude))
        ? Number(address.latitude)
        : null,
    longitude:
      address?.longitude != null && Number.isFinite(Number(address.longitude))
        ? Number(address.longitude)
        : null,
    googleMapsUri: trimmed(address?.googleMapsUri),
    street: trimmed(address?.street),
    district: trimmed(
      address?.district || address?.neighborhood || address?.colonia
    ),
    city: trimmed(address?.city),
    state: trimmed(address?.state),
    postalCode: trimmed(address?.postalCode),
    country: trimmed(address?.country || base.country).toUpperCase(),
    email: trimmedLower(address?.email),
    phone: trimmed(address?.phone),
    isPrimary: !!address?.isPrimary,
  };
}

export function addressRowKey(address, index) {
  return (
    trimmed(address?.clientId) ||
    trimmed(address?.addressId) ||
    (address?.id != null ? String(address.id) : "") ||
    `index-${index}`
  );
}

export function formatAddressLine(address) {
  const parts = [
    trimmed(address?.street),
    trimmed(address?.city),
    trimmed(address?.state),
    trimmed(address?.postalCode),
    trimmed(address?.country),
  ].filter(Boolean);
  return parts.join(", ");
}

export function savedAddressLookupLabel(address) {
  return trimmed(address?.formattedAddress);
}

export function isAddressRowFilled(address) {
  return [
    address?.label,
    address?.name,
    address?.formattedAddress,
    address?.street,
    address?.district,
    address?.city,
    address?.state,
    address?.postalCode,
    address?.country,
    address?.email,
    address?.phone,
  ].some((value) => trimmed(value));
}

export function isVerifiedAddressRow(address) {
  return !!trimmed(address?.formattedAddress);
}

export function stripAddressRowForSave(address) {
  const row = normalizeAddressRow(address);
  return {
    ...(row.id ? { id: row.id } : {}),
    label: row.label,
    name: row.name,
    formattedAddress: row.formattedAddress,
    placeId: row.placeId,
    businessName: row.businessName,
    latitude: row.latitude,
    longitude: row.longitude,
    googleMapsUri: row.googleMapsUri,
    street: row.street,
    district: row.district,
    city: row.city,
    state: row.state,
    postalCode: row.postalCode,
    country: row.country,
    email: row.email,
    phone: row.phone,
    isPrimary: !!row.isPrimary,
  };
}

export function applyAddressCandidate(address, candidate) {
  const mapped =
    candidate?.address && typeof candidate.address === "object"
      ? candidate.address
      : candidate || {};
  const nextCountry = trimmed(
    mapped.country || candidate?.country
  ).toUpperCase();
  return {
    ...address,
    formattedAddress: trimmed(candidate?.formattedAddress),
    placeId: trimmed(candidate?.placeId),
    businessName: trimmed(candidate?.businessName),
    latitude:
      candidate?.latitude != null && Number.isFinite(Number(candidate.latitude))
        ? Number(candidate.latitude)
        : null,
    longitude:
      candidate?.longitude != null &&
      Number.isFinite(Number(candidate.longitude))
        ? Number(candidate.longitude)
        : null,
    googleMapsUri: trimmed(candidate?.googleMapsUri),
    street: trimmed(mapped.street),
    district: trimmed(mapped.district || mapped.neighborhood || mapped.colonia),
    city: trimmed(mapped.city),
    state: trimmed(mapped.state),
    postalCode: trimmed(mapped.postalCode),
    country: nextCountry || trimmed(address?.country).toUpperCase(),
  };
}

export function getAddressLookupErrorMessage(error) {
  const data = error?.response?.data;
  if (typeof data?.error === "string") return data.error;
  if (typeof data?.error?.message === "string") return data.error.message;
  if (typeof data?.message === "string") return data.message;
  if (typeof error?.message === "string") return error.message;
  return "Unable to look up addresses right now.";
}

export function useStorefrontAddressLookup() {
  const [draftsByKey, setDraftsByKey] = useState({});
  const [candidatesByKey, setCandidatesByKey] = useState({});
  const [loadingByKey, setLoadingByKey] = useState({});
  const [errorsByKey, setErrorsByKey] = useState({});

  const timersRef = useRef({});
  const requestSeqRef = useRef({});

  const clearTimer = useCallback((rowKey) => {
    if (timersRef.current[rowKey]) {
      clearTimeout(timersRef.current[rowKey]);
      delete timersRef.current[rowKey];
    }
  }, []);

  useEffect(() => {
    return () => {
      Object.values(timersRef.current).forEach((timer) => clearTimeout(timer));
      timersRef.current = {};
    };
  }, []);

  const syncRows = useCallback(
    (rows, getRowKey, formatter = savedAddressLookupLabel) => {
      const activeKeys = new Set(
        (rows || []).map((row, index) => getRowKey(row, index))
      );

      setDraftsByKey((prev) => {
        const next = {};
        (rows || []).forEach((row, index) => {
          const rowKey = getRowKey(row, index);
          next[rowKey] = prev[rowKey] ?? formatter(row);
        });
        return next;
      });

      const filterMap = (prev) =>
        Object.fromEntries(
          Object.entries(prev).filter(([rowKey]) => activeKeys.has(rowKey))
        );

      setCandidatesByKey(filterMap);
      setLoadingByKey(filterMap);
      setErrorsByKey(filterMap);

      Object.keys(timersRef.current).forEach((rowKey) => {
        if (!activeKeys.has(rowKey)) {
          clearTimer(rowKey);
          delete requestSeqRef.current[rowKey];
        }
      });
    },
    [clearTimer]
  );

  const setDraftForRow = useCallback(
    (rowKey, value) => {
      const nextValue = String(value || "");
      const query = trimmed(nextValue);
      const requestSeq = (requestSeqRef.current[rowKey] || 0) + 1;
      requestSeqRef.current[rowKey] = requestSeq;

      setDraftsByKey((prev) => ({ ...prev, [rowKey]: nextValue }));
      setErrorsByKey((prev) => {
        const next = { ...prev };
        delete next[rowKey];
        return next;
      });

      clearTimer(rowKey);

      if (query.length < MIN_ADDRESS_LOOKUP_CHARS) {
        setCandidatesByKey((prev) => ({ ...prev, [rowKey]: [] }));
        setLoadingByKey((prev) => ({ ...prev, [rowKey]: false }));
        return;
      }

      timersRef.current[rowKey] = setTimeout(async () => {
        setLoadingByKey((prev) => ({ ...prev, [rowKey]: true }));
        try {
          const response = await post("/api/storefront/address/lookup", {
            addressLine: query,
          });
          if (requestSeqRef.current[rowKey] !== requestSeq) return;
          const candidates = Array.isArray(response?.candidates)
            ? response.candidates
            : [];
          setCandidatesByKey((prev) => ({ ...prev, [rowKey]: candidates }));
        } catch (error) {
          if (requestSeqRef.current[rowKey] !== requestSeq) return;
          setCandidatesByKey((prev) => ({ ...prev, [rowKey]: [] }));
          setErrorsByKey((prev) => ({
            ...prev,
            [rowKey]: getAddressLookupErrorMessage(error),
          }));
        } finally {
          if (requestSeqRef.current[rowKey] === requestSeq) {
            setLoadingByKey((prev) => ({ ...prev, [rowKey]: false }));
          }
        }
      }, ADDRESS_LOOKUP_DEBOUNCE_MS);
    },
    [clearTimer]
  );

  const applyCandidateForRow = useCallback((rowKey, candidate) => {
    setDraftsByKey((prev) => ({
      ...prev,
      [rowKey]: trimmed(candidate?.formattedAddress),
    }));
    setCandidatesByKey((prev) => ({ ...prev, [rowKey]: [] }));
    setLoadingByKey((prev) => ({ ...prev, [rowKey]: false }));
    setErrorsByKey((prev) => {
      const next = { ...prev };
      delete next[rowKey];
      return next;
    });
  }, []);

  const clearRow = useCallback(
    (rowKey) => {
      clearTimer(rowKey);
      delete requestSeqRef.current[rowKey];
      setDraftsByKey((prev) => {
        const next = { ...prev };
        delete next[rowKey];
        return next;
      });
      setCandidatesByKey((prev) => {
        const next = { ...prev };
        delete next[rowKey];
        return next;
      });
      setLoadingByKey((prev) => {
        const next = { ...prev };
        delete next[rowKey];
        return next;
      });
      setErrorsByKey((prev) => {
        const next = { ...prev };
        delete next[rowKey];
        return next;
      });
    },
    [clearTimer]
  );

  return {
    draftsByKey,
    candidatesByKey,
    loadingByKey,
    errorsByKey,
    syncRows,
    setDraftForRow,
    applyCandidateForRow,
    clearRow,
  };
}
